import {
  inventoryMutations,
  mutationBlobKey,
} from './inventoryMutationCoordinator';
import { ApplicationError, toApplicationError } from '../domain/errors';
import { InventoryAuthority } from '@qkindex/contracts';
import { CreateItemInput, UpdateItemInput } from '../domain/item';
import { imageOperationService } from './imageOperationService';
import {
  ItemRepository,
  firebaseItemRepository,
} from '../repositories/itemRepository';
import { validateKeywords } from '../domain/keywords';
import { createOperationId } from '../domain/operationId';
import { bulkOperationService } from './bulkOperationService';

export interface ItemService {
  createItem(
    authority: InventoryAuthority,
    input: CreateItemInput,
    operationId?: string,
  ): Promise<string>;
  updateItem(
    authority: InventoryAuthority,
    itemId: string,
    input: UpdateItemInput,
  ): Promise<void>;
  moveItemToBin(authority: InventoryAuthority, itemId: string): Promise<void>;
  restoreItem(authority: InventoryAuthority, itemId: string): Promise<void>;
  replaceImage(
    authority: InventoryAuthority,
    itemId: string,
    image: Blob,
    fields?: UpdateItemInput,
    operationId?: string,
  ): Promise<void>;
  clearImage(authority: InventoryAuthority, itemId: string): Promise<void>;
}

export const createItemService = (
  items: ItemRepository,
  operations: typeof imageOperationService,
): ItemService => {
  const create = async (
    authority: InventoryAuthority,
    input: CreateItemInput,
    suppliedOperationId: string | undefined,
  ): Promise<string> => {
    if (!authority.userId || !authority.collectionId) {
      throw new ApplicationError(
        'authentication',
        'Sign in before saving an item.',
      );
    }
    if (!input.location.trim() || !Number.isFinite(input.qty)) {
      throw new ApplicationError(
        'validation',
        'Keywords, location, and quantity are required.',
      );
    }
    const keywords = validateKeywords(input.keywords);
    const operationId = suppliedOperationId || createOperationId();

    try {
      const result = await operations.submit<{ itemId: string }>(
        {
          action: 'create-item',
          collectionId: authority.collectionId,
          operationId,
          fields: {
            keywords,
            location: input.location.trim(),
            qty: input.qty,
            ...(input.locationId ? { locationId: input.locationId } : {}),
            ...(input.tagIds === undefined ? {} : { tagIds: input.tagIds }),
          },
          ...(input.sourceItemId ? { sourceItemId: input.sourceItemId } : {}),
        },
        input.image,
      );
      return result.itemId;
    } catch (error) {
      throw toApplicationError(error, 'Unable to save the item.');
    }
  };

  return {
    createItem: create,
    updateItem: async (authority, itemId, input) => {
      await items.update(authority, itemId, {
        ...input,
        ...(typeof input.keywords === 'string'
          ? { keywords: validateKeywords(input.keywords) }
          : {}),
      });
    },
    async moveItemToBin(authority, itemId) {
      await bulkOperationService.updateItems(authority, [
        { id: itemId, updates: { deleted: true } },
      ]);
    },
    restoreItem: async (authority, itemId) => {
      await items.update(authority, itemId, { deleted: false });
    },
    async replaceImage(authority, itemId, image, fields, suppliedOperationId) {
      const { expectedRevision, ...editable } = fields || {};
      const payload = fields
        ? {
            ...editable,
            ...(fields.keywords !== undefined
              ? { keywords: validateKeywords(fields.keywords) }
              : {}),
          }
        : undefined;
      await inventoryMutations.run(
        authority.userId,
        [{ collectionId: authority.collectionId, itemIds: [itemId] }],
        JSON.stringify([
          'replace-image',
          authority.collectionId,
          itemId,
          payload,
          expectedRevision,
          mutationBlobKey(image),
        ]),
        'Save item and image',
        async (operationId) => {
          await operations.submit(
            {
              action: 'replace-image',
              collectionId: authority.collectionId,
              operationId,
              itemIds: [itemId],
              ...(payload ? { fields: payload } : {}),
              ...(expectedRevision === undefined
                ? {}
                : { expectedRevisions: { [itemId]: expectedRevision } }),
            },
            image,
          );
        },
        suppliedOperationId,
      );
    },
    async clearImage(authority, itemId) {
      try {
        await items.clearImage(authority, itemId);
      } catch (error) {
        throw toApplicationError(error, 'Unable to remove the item image.');
      }
    },
  };
};

export const itemService = createItemService(
  firebaseItemRepository,
  imageOperationService,
);
